import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';

import styles from './RowActionsRender.module.css';

import { Popover } from '../../../store/popover/PopoverContext';
import IconButton from '../../atoms/IconButton/IconButton';
import { PopoverTrigger, PopoverContent } from '../../atoms/Popover/Popover';
import useTableRowActions from '../../../domain/tableRowActions/useTableRowActions';
import { getQuickRowActions } from '../../../domain/tableRowActions/tableRowActionsUseCase';
import RowActionsMenu from '../RowActionsMenu/RowActionsMenu';
import DeleteRowsConfirmModal from '../DeleteRowsConfirmModal/DeleteRowsConfirmModal';

/**
 * Row quick actions bar (icons + full menu)
 * @param {object} props
 * @param {string} props.rowId - The row id
 * @param {ApiManagerTableActionsArray} props.rowActions - The row actions
 * @param {boolean} [props.disabled=false] - Whether to disable the row actions
 * @param {(formUrl: string) => void} [props.onOpenFormModal] - Handle opening form modal from row action
 * @param {(isOpen: boolean) => void} [props.onOpenStateChange] - Notify when a popover or delete modal is open
 * @returns {React.ReactNode}
 */
function RenderRowActions({
  rowId = '',
  rowActions,
  disabled = false,
  onOpenFormModal,
  onOpenStateChange,
}) {
  const { t } = useTranslation();
  const {
    isMutationLoading,
    handleRowActionByType,
    rowActionsIsOpen,
    setRowActionsIsOpen,
    isDeleteConfirmOpen,
    handleCloseDeleteConfirmModal,
    confirmDeleteRow,
    isDeleteLinePending,
  } = useTableRowActions({ rowId, onOpenFormModal });

  const quickActions = useMemo(
    () => getQuickRowActions(rowActions, rowId),
    [rowActions, rowId],
  );

  const [openQuickSubActionId, setOpenQuickSubActionId] = useState(/** @type {string | null} */ (null));

  useEffect(() => {
    const isInteractionOpen = rowActionsIsOpen
      || isDeleteConfirmOpen
      || openQuickSubActionId !== null;
    onOpenStateChange?.(isInteractionOpen);
  }, [
    rowActionsIsOpen,
    isDeleteConfirmOpen,
    openQuickSubActionId,
    onOpenStateChange,
  ]);

  const handleSelectAction = (action, type) => {
    handleRowActionByType(action, type);
    setRowActionsIsOpen(false);
    setOpenQuickSubActionId(null);
  };

  return (
    <>
      <div
        className={styles['row-actions-bar']}
        role="toolbar"
        aria-label={t('tableRowActions.toolbarAriaLabel')}
      >
        {quickActions.map((action) => {
          if (action.type === 'openSubActions') {
            const subAction = /** @type {ApiManagerTableActionOpenSubActionsObject} */ (action);
            const isSubOpen = openQuickSubActionId === action.id;
            return (
              <Popover
                key={action.id}
                open={isSubOpen}
                onOpenChange={(open) => {
                  setOpenQuickSubActionId(open ? action.id : null);
                }}
                placement="bottom-end"
              >
                <PopoverTrigger asChild>
                  <IconButton
                    icon={action.icon}
                    color="transparent"
                    size="medium"
                    title={action.lib}
                    onClick={() => {
                      if (!disabled) {
                        setOpenQuickSubActionId(action.id);
                      }
                    }}
                    disabled={isMutationLoading || disabled}
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <RowActionsMenu
                    key={isSubOpen ? action.id : `${action.id}-closed`}
                    rowId={rowId}
                    actions={subAction.actions}
                    isLoading={isMutationLoading}
                    onSelectAction={handleSelectAction}
                  />
                </PopoverContent>
              </Popover>
            );
          }

          return (
            <IconButton
              key={action.id}
              icon={action.icon}
              color="transparent"
              size="medium"
              title={action.lib}
              onClick={() => {
                if (!disabled) {
                  handleRowActionByType(action, action.type);
                }
              }}
              disabled={isMutationLoading || disabled}
            />
          );
        })}
        <Popover
          open={rowActionsIsOpen}
          onOpenChange={setRowActionsIsOpen}
          placement="bottom-end"
        >
          <PopoverTrigger asChild>
            <IconButton
              icon="more-vertical"
              color="transparent"
              size="medium"
              title={t('tableRowActions.moreActions')}
              onClick={() => {
                if (!disabled) {
                  setRowActionsIsOpen(true);
                }
              }}
              disabled={isMutationLoading || disabled}
            />
          </PopoverTrigger>
          <PopoverContent>
            <RowActionsMenu
              key={rowActionsIsOpen ? `${rowId}-menu-open` : `${rowId}-menu-closed`}
              rowId={rowId}
              actions={rowActions ?? []}
              isLoading={isMutationLoading}
              onSelectAction={handleSelectAction}
            />
          </PopoverContent>
        </Popover>
      </div>
      <DeleteRowsConfirmModal
        id={`modal-delete-row-confirm-${rowId}`}
        isOpen={isDeleteConfirmOpen}
        onClose={handleCloseDeleteConfirmModal}
        onConfirm={confirmDeleteRow}
        count={1}
        isLoading={isDeleteLinePending}
      />
    </>
  );
}

export default RenderRowActions;
